import { getActiveUser } from "@noom/hive-auth";
import { BuyflowPurchaseIntentTriggeredProto } from "@noom/noom-contracts/noom_contracts/events/buyflow/marketing";
import getStore from "src/utils/redux/store";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import {
  acceptedNoomClinicalOffer,
  shouldFireWholeHealthPixels,
} from "src/utils/userSegment/features";
import { appConfig } from "src/config";
import {
  CheckoutOrderEntry,
  setPrefetchedEltv,
  setPrefetchedShopEltv,
} from "src/utils/redux/slices/checkoutOrders";
import { RecommendedPlanState } from "src/utils/redux/slices/recommendedPlan";
import { fetchEltv, ComputeEltvRequest } from "src/utils/api/marketingApi";
import { createBuyflowPurchaseIntentEvent } from "src/utils/services/ConversionTracker";
import { trackEvent } from "src/utils/api/tracker";
import { captureException } from "src/utils/error";
import { isNotFoundError } from "src/utils/fetch";
import { convertNumberToMoney } from "src/utils/noomContracts/moneyProto";
import { convertCyclesToPlan } from "src/utils/orderConversions";
import { getShopProductByCartKey } from "src/utils/shopProducts";
import { parseDobInputToLocalNoon } from "src/utils/dobUtils";
import { calculateAge } from "src/utils/datetime";
import { getCheckoutDateOfBirthValue } from "src/hooks/checkout/useCheckoutDateOfBirth";

function getEltvUserFields(
  intentEvent: BuyflowPurchaseIntentTriggeredProto
): ComputeEltvRequest {
  return {
    userId: intentEvent.userId,
    email: intentEvent.email,
    age: intentEvent.age,
    gender: intentEvent.gender,
    countryCode: intentEvent.countryCode,
    state: intentEvent.state,
    currentBmi: intentEvent.currentBmi,
    hasHealthRisk: intentEvent.hasHealthRisk,
    hasImportantDate: intentEvent.hasImportantDate,
    tookWeightLossMeds: intentEvent.tookWeightLossMeds,
    weightLossGoal: intentEvent.weightLossGoal,
  };
}

function getEltvPlanFields(
  intentEvent: BuyflowPurchaseIntentTriggeredProto
): Partial<ComputeEltvRequest> {
  return {
    planId: intentEvent.planId,
    planRecurringPrice: intentEvent.planRecurringPrice,
    planBillingCycle: intentEvent.planBillingCycle,
    trialPeriod: intentEvent.trialPeriod,
    paidAtSignup: intentEvent.paidAtSignup,
    trialFee: intentEvent.trialFee,
    discount: intentEvent.discount,
  };
}

function buildEltvRequestFromState(): ComputeEltvRequest | null {
  const state = getStore().getState();
  const { recommendedPlan, userData, paymentEnrollmentForm, checkoutOrders } =
    state;

  const email = paymentEnrollmentForm?.enrollmentInfo?.email;
  if (!email) {
    return null;
  }

  const surveyAnswers = getSurveyAnswers(state);
  const { initialBillingCycle, recurringBillingCycle } = checkoutOrders;

  const intentEvent = createBuyflowPurchaseIntentEvent(
    email,
    recommendedPlan,
    surveyAnswers,
    userData,
    initialBillingCycle,
    recurringBillingCycle
  );

  return {
    ...getEltvUserFields(intentEvent),
    ...getEltvPlanFields(intentEvent),
  };
}

/**
 * The marketing service picks the model inputs from the product name. One-time
 * products have no billing cycles, so they are sent without plan fields.
 */
function buildShopEltvRequest(
  entry: CheckoutOrderEntry,
  email: string
): ComputeEltvRequest {
  const state = getStore().getState();
  const { initialBillingCycle, recurringBillingCycle } = entry;
  const plan = recurringBillingCycle
    ? convertCyclesToPlan({
        initialCycle: initialBillingCycle,
        recurringCycle: recurringBillingCycle,
        trialSaleItemId: entry.saleItem?.trialSaleItemId,
      })
    : undefined;

  const intentEvent = createBuyflowPurchaseIntentEvent(
    email,
    (plan ?? {}) as RecommendedPlanState,
    getSurveyAnswers(state),
    state.userData,
    initialBillingCycle,
    recurringBillingCycle
  );

  return {
    ...getEltvUserFields(intentEvent),
    age: getShopAge(),
    ...(plan
      ? {
          ...getEltvPlanFields(intentEvent),
          // The intent event reads the promo state, which shop checkout never
          // applies, so any promo there is left over from another flow.
          discount: convertNumberToMoney(0, plan.currency),
        }
      : {}),
    productName: entry.productName,
  };
}

/**
 * Shop surveys collect a date of birth, never the weight survey's `ageRange`,
 * so an `ageRange` in a shop session is left over from another survey.
 */
function getShopAge(): number | undefined {
  const dateOfBirth = parseDobInputToLocalNoon(getCheckoutDateOfBirthValue());
  return dateOfBirth ? calculateAge(dateOfBirth) : undefined;
}

/**
 * Pre-fetches the ML-computed ELTV from the marketing service for med users.
 * Runs as fire-and-forget after checkout initialization. The result is stored
 * in Redux for later use during the T&C agreement (shadow-mode logging).
 */
export async function prefetchEltvIfEligible(): Promise<void> {
  if (!acceptedNoomClinicalOffer() || !appConfig.MED_PIXEL_TRACKING_ENABLED) {
    return;
  }

  getStore().dispatch(setPrefetchedEltv(undefined));

  try {
    const request = buildEltvRequestFromState();
    if (!request) {
      getStore().dispatch(setPrefetchedEltv(null));
      return;
    }

    const response = await fetchEltv(request);
    getStore().dispatch(
      setPrefetchedEltv({
        eltvValue: response.eltvValue,
        eltvBlendedValue: response.eltvBlendedValue,
      })
    );
    trackEvent("EltvPrefetchCompleted", {
      eltvValue: response.eltvValue,
      eltvBlendedValue: response.eltvBlendedValue,
      planId: request.planId,
    });
  } catch (err) {
    getStore().dispatch(setPrefetchedEltv(null));
    captureException(err);
    trackEvent("EltvPrefetchFailed", {
      error: err?.message || "unknown",
    });
  }
}

async function prefetchShopEltv(
  entry: CheckoutOrderEntry,
  email: string
): Promise<void> {
  const { productName } = entry;
  getStore().dispatch(setPrefetchedShopEltv({ productName, eltv: undefined }));

  try {
    const response = await fetchEltv(buildShopEltvRequest(entry, email));
    getStore().dispatch(
      setPrefetchedShopEltv({
        productName,
        eltv: {
          eltvValue: response.eltvValue,
          eltvBlendedValue: response.eltvBlendedValue,
        },
      })
    );
    trackEvent("EltvPrefetchCompleted", {
      eltvValue: response.eltvValue,
      eltvBlendedValue: response.eltvBlendedValue,
      productName,
    });
  } catch (err) {
    // The marketing service answers 404 for unsupported products, which use
    // the plan price instead.
    if (!isNotFoundError(err)) {
      captureException(err);
    }
    getStore().dispatch(setPrefetchedShopEltv({ productName, eltv: null }));
    trackEvent("EltvPrefetchFailed", {
      error: err?.message || "unknown",
      productName,
    });
  }
}

/**
 * Pre-fetches the ML-computed ELTV of each whole health (shop) cart product,
 * keyed by product name, for the shop purchase intent value. Fire-and-forget
 * once the shop checkout's first real orders exist, since the plan fields come
 * from their billing cycles.
 */
export async function prefetchShopEltvsIfEligible(
  entries: CheckoutOrderEntry[]
): Promise<void> {
  if (!shouldFireWholeHealthPixels() || !appConfig.MED_PIXEL_TRACKING_ENABLED) {
    return;
  }

  // Shop checkout has no email field and requires a signed-in user.
  const email = getActiveUser()?.email;
  if (!email) {
    return;
  }

  await Promise.all(
    entries
      .filter((entry) => getShopProductByCartKey(entry.productName))
      .map((entry) => prefetchShopEltv(entry, email))
  );
}
